import React, { useState } from "react";

// material-ui
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Tooltip,
  FormControlLabel,
  useTheme,
  Autocomplete,
} from "@mui/material";

// third-party
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// project imports
import CircularWithPath from "../../../../../components/@extended/progress/CircularWithPath";
import { openSnackbar } from "../../../../../api/snackbar";
import ReusableModal from "../../../../../components/ReusableComponent/ReusableModal";

// assets
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

// types
import { SnackbarProps } from "../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";

// hooks
import { useUsers } from "../../../../../hooks/query-hooks/useUsers";
import { useResumesForAutocomplete } from "../../../../../hooks/query-hooks/useResumes";
import { useResumeSubscriptionForAutocomplete } from "../../../../../hooks/query-hooks/useResumeSubscription";
import { Resume } from "../../../../../types/resumes-managment/resume";
import { useDebounce } from "../../../../../hooks/useDebounce";
import {
  PurchasedResume,
  PurchasedResumeFormData,
} from "../../../../../types/orders-managment/resume";

const getInitialValues = (
  purchasedResume: PurchasedResume | null
): PurchasedResumeFormData => {
  if (purchasedResume) {
    return {
      user_id: purchasedResume.user_id?.toString() || "",
      resume_id: purchasedResume.resume_id?.toString() || "",
      resume_subscription_id:
        purchasedResume.resume_subscription_id?.toString() || "",
      status: purchasedResume.status || false,
    };
  } else {
    return {
      user_id: "",
      resume_id: "",
      resume_subscription_id: "",
      status: false,
    };
  }
};

// ==============================|| PURCHASED RESUME ADD / EDIT - FORM ||============================== //

const AddEditPurchasedResume = ({
  open,
  purchasedResume,
  id,
  loading,
  fetchData,
  setLoading,
  handleClose,
  setResult,
}: {
  open: boolean;
  loading?: boolean;
  id?: number;
  setLoading?: any;
  fetchData: any;
  purchasedResume: PurchasedResume | null;
  handleClose: () => void;
  setResult: any;
}) => {
  const intl = useIntl();
  const theme = useTheme();

  const [serverErrors, setServerErrors] = useState<any>({});
  const [resumeSearchTerm, setResumeSearchTerm] = useState("");
  const [subscriptionSearchTerm, setSubscriptionSearchTerm] = useState("");
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<any | null>(
    null
  );

  const debouncedResumeSearchTerm = useDebounce(resumeSearchTerm, 500);
  const debouncedSubscriptionSearchTerm = useDebounce(
    subscriptionSearchTerm,
    500
  );

  const {
    data: companies = [],
    isLoading: companiesLoading,
    error: companiesError,
  } = useUsers("COMPANY");

  const {
    items: resumes,
    loading: resumesLoading,
    loadMore: loadMoreResumes,
    hasMore: hasMoreResumes,
  } = useResumesForAutocomplete(20, debouncedResumeSearchTerm);

  const {
    items: resumeSubscriptions,
    loading: subscriptionsLoading,
    loadMore: loadMoreSubscriptions,
    hasMore: hasMoreSubscriptions,
  } = useResumeSubscriptionForAutocomplete(20, debouncedSubscriptionSearchTerm);

  const PurchasedResumeSchema = Yup.object().shape({
    user_id: Yup.string().required("Company is required"),
    resume_id: Yup.string().required("Resume is required"),
    resume_subscription_id: Yup.string().required(
      "Resume Subscription is required"
    ),
    status: Yup.boolean().required("Status is required"),
  });

  const formik = useFormik({
    initialValues: getInitialValues(purchasedResume!),
    validationSchema: PurchasedResumeSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setServerErrors({});

      try {
        const purchasedResumeData = {
          user_id: parseInt(values.user_id),
          resume_id: parseInt(values.resume_id),
          resume_subscription_id: parseInt(values.resume_subscription_id),
          status: values.status ? "1" : "0",
        };

        if (purchasedResume) {
          try {
            const response = await USERAXIOS.post(
              `/purchased_resumes/${purchasedResume.id}`,
              {
                resume_id: parseInt(values.resume_id),
                status: values.status ? "1" : "0",
              }
            );
            const updatedPurchasedResume = response.data.data;

            if (updatedPurchasedResume) {
              setResult((prev: any[]) =>
                prev.map((item) =>
                  item.id === updatedPurchasedResume.id
                    ? updatedPurchasedResume
                    : item
                )
              );

              openSnackbar({
                open: true,
                message: "Purchased Resume updated successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: { color: "success" },
              } as SnackbarProps);
            }
            handleClose();
          } catch (error: any) {
            const fieldErrors =
              error?.response?.data?.errors || error?.response?.data?.message;
            if (fieldErrors && typeof fieldErrors === "object") {
              setServerErrors(fieldErrors);
            }

            openSnackbar({
              open: true,
              message:
                typeof error?.response?.data?.message === "string"
                  ? error.response.data.message
                  : "Failed to update purchased resume",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "error" },
            } as SnackbarProps);
          }
        } else {
          try {
            const response = await USERAXIOS.post(
              `/purchased_resumes`,
              purchasedResumeData
            );
            const newPurchasedResume = response.data.data;

            if (newPurchasedResume) {
              setResult((prev: any[]) => [...prev, newPurchasedResume]);

              openSnackbar({
                open: true,
                message: "Purchased Resume added successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: { color: "success" },
              } as SnackbarProps);
            }
            handleClose();
          } catch (error: any) {
            const fieldErrors =
              error?.response?.data?.errors || error?.response?.data?.message;
            if (fieldErrors && typeof fieldErrors === "object") {
              setServerErrors(fieldErrors);
            }

            openSnackbar({
              open: true,
              message:
                typeof error?.response?.data?.message === "string"
                  ? error.response.data.message
                  : "Failed to create purchased resume",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "error" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
      } catch (error: any) {
        console.error("Error submitting form:", error);
        openSnackbar({
          open: true,
          message:
            typeof error?.response?.data?.message === "string"
              ? error.response.data.message
              : "An error occurred",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  if (loading) {
    return (
      <Box sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="center">
          <CircularWithPath />
        </Stack>
      </Box>
    );
  }

  return (
    <ReusableModal open={open} handleClose={handleClose}>
      <FormikProvider value={formik}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle
              color="primary"
              style={{
                textAlign: "center",
                fontSize: "22px",
                fontWeight: "bold",
                marginBottom: "10px",
              }}
            >
              {purchasedResume ? (
                <FormattedMessage id="Edit Purchased Resume" />
              ) : (
                <FormattedMessage id="Add Purchased Resume" />
              )}
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ p: 2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="user_id">
                      <FormattedMessage id="Company" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Select company"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <Select
                      id="user_id"
                      displayEmpty
                      {...getFieldProps("user_id")}
                      onChange={(event: SelectChangeEvent<string>) => {
                        setFieldValue("user_id", event.target.value);
                      }}
                      error={Boolean(touched.user_id && errors.user_id)}
                      disabled={companiesLoading || !!purchasedResume}
                    >
                      {companiesLoading ? (
                        <MenuItem disabled>Loading companies...</MenuItem>
                      ) : companiesError ? (
                        <MenuItem disabled>Error loading companies</MenuItem>
                      ) : companies.length === 0 ? (
                        <MenuItem disabled>No companies available</MenuItem>
                      ) : (
                        companies.map((company: any) => (
                          <MenuItem
                            key={company.id}
                            value={company.id.toString()}
                          >
                            {company.user_name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {touched.user_id && errors.user_id && (
                      <FormHelperText error>{errors.user_id}</FormHelperText>
                    )}
                    {serverErrors.user_id && (
                      <FormHelperText error>
                        {serverErrors.user_id}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="resume_id">
                      <FormattedMessage id="Resume" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Select resume"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <Autocomplete
                      options={resumes}
                      getOptionLabel={(option) =>
                        option.objectives || option.id?.toString() || ""
                      }
                      value={selectedResume}
                      onChange={(_, newValue) => {
                        setSelectedResume(newValue);
                        setFieldValue(
                          "resume_id",
                          newValue?.id?.toString() || ""
                        );
                      }}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "input") {
                          setResumeSearchTerm(newInputValue);
                        }
                      }}
                      loading={resumesLoading}
                      filterOptions={(x) => x}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search for resumes"
                          error={Boolean(touched.resume_id && errors.resume_id)}
                          helperText={touched.resume_id && errors.resume_id}
                        />
                      )}
                      ListboxProps={{
                        onScroll: (event) => {
                          const listboxNode = event.currentTarget;
                          if (
                            listboxNode.scrollTop + listboxNode.clientHeight >=
                            listboxNode.scrollHeight - 1
                          ) {
                            if (hasMoreResumes) {
                              loadMoreResumes();
                            }
                          }
                        },
                      }}
                    />
                    {serverErrors.resume_id && (
                      <FormHelperText error>
                        {serverErrors.resume_id}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="resume_subscription_id">
                      <FormattedMessage id="Resume Subscription" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Select resume subscription"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <Autocomplete
                      options={resumeSubscriptions}
                      getOptionLabel={(option) =>
                        `Subscription ${option.id} - ${option.amount_resumes} resumes` ||
                        ""
                      }
                      value={selectedSubscription}
                      onChange={(_, newValue) => {
                        setSelectedSubscription(newValue);
                        setFieldValue(
                          "resume_subscription_id",
                          newValue?.id?.toString() || ""
                        );
                      }}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "input") {
                          setSubscriptionSearchTerm(newInputValue);
                        }
                      }}
                      loading={subscriptionsLoading}
                      filterOptions={(x) => x}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search for subscriptions"
                          error={Boolean(
                            touched.resume_subscription_id &&
                              errors.resume_subscription_id
                          )}
                          helperText={
                            touched.resume_subscription_id &&
                            errors.resume_subscription_id
                          }
                        />
                      )}
                      ListboxProps={{
                        onScroll: (event) => {
                          const listboxNode = event.currentTarget;
                          if (
                            listboxNode.scrollTop + listboxNode.clientHeight >=
                            listboxNode.scrollHeight - 1
                          ) {
                            if (hasMoreSubscriptions) {
                              loadMoreSubscriptions();
                            }
                          }
                        },
                      }}
                    />
                    {serverErrors.resume_subscription_id && (
                      <FormHelperText error>
                        {serverErrors.resume_subscription_id}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="status">
                      <FormattedMessage id="Status" />
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip
                          title={
                            "Set the status (sends 1 for active, 0 for inactive)"
                          }
                        >
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <FormControl fullWidth>
                      <FormControlLabel
                        control={
                          <Switch
                            id="status"
                            checked={formik.values.status}
                            {...getFieldProps("status")}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setFieldValue("status", checked);
                            }}
                          />
                        }
                        label={
                          formik.values.status ? (
                            <FormattedMessage id="Active" />
                          ) : (
                            <FormattedMessage id="Inactive" />
                          )
                        }
                      />
                    </FormControl>
                    {touched.status && errors.status && (
                      <FormHelperText error sx={{ pl: 1.75 }}>
                        {errors.status}
                      </FormHelperText>
                    )}
                    {serverErrors.status && (
                      <FormHelperText error sx={{ pl: 1.75 }}>
                        {serverErrors.status}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={6}>
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage
                        id={purchasedResume ? "Update" : "Submit"}
                      />
                    </Button>
                  </Grid>
                  <Grid item md={6}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
    </ReusableModal>
  );
};

export default AddEditPurchasedResume;
